/**
 * Shows driver manuals when user searches.
 *
 * @module feature/PaccarPartsApiSearch
 */
import AjaxHandler from 'AjaxHandler';
import 'bootstrap/js/dist/collapse.js';

/**
 * Loads driver manua data, populates manual list and expands results list.
 *
 * @requires module:project/Common.AjaxHandler
 * @requires bootstrap/js/dist/collapse
 * @memberof module:feature/PaccarPartsApiSearch
 * @version 1.0.0
 * @author Peter van Gennip <peter.van.gennip@valtech.nl>
 */

class PaccarPartsApiSearch {
    /**
     * Initializes internal variables, configuration.
     * @param {jQueryElememt} $element DOM Element to be upgraded.
     * @param {object} options Configuration options.
     *
     */
    constructor($element) {
        this.$element = $($element);
        this.data = null;
        this.lang = this.$element.attr('data-lang');
        this.introSlideTemplate = null;
        this.searchedChassisNumber = '';
        this.$tpl = this.$element.find('.js-tpl').clone().html();
        this.$searchForm = this.$element.find('.js-parts-search-form');
        this.$searchResultsList = this.$element.find('.js-search-results-list');
        this.$loader = this.$element.find('.loader');
        this.$chassisNumber = this.$element.find('.js-parts-search-chassisNumber');
        this.$itemId = this.$element.find('.js-parts-search-itemid');
        this.$results = this.$element.find('.js-parts-search-results');
        this.$resultTitle = this.$element.find('.js-parts-search-result-title');
        this.$resultBody = this.$element.find('.js-parts-search-result-body');
        this.$error = this.$element.find('.js-parts-search-error');
        this.collapsible = this.$element.find('.collapse');
        this.$vehicleUpgrades = this.$element.find('.js-parts-search-vehicle-upgrades');
        this.$followUp = this.$element.find('.js-parts-search-follow-up');
        this.$followUpText = this.$element.find('.js-parts-search-follow-up-text');
        this.$followUpActions = this.$element.find('.js-parts-search-follow-up-actions');

        //defaults
        this.config = {
            apiSearch: 'api/feature/paccarpartsapi/search',
            lang: this.lang,
        };

        //init ajax helper
        this.ajax = new AjaxHandler({
            type: 'POST',
        });
    }

    /**
     * Initialize.
     */
    init() {
        // remove tpl class
        $('.tpl').detach();

        this.$searchForm.submit((e) => {
            e.preventDefault();

            const isValid = this.$searchForm[0].checkValidity();
            if (isValid) {
                this.$loader.fadeIn(150);
                this.searchedChassisNumber = this.$chassisNumber.val();
                this.getData()
                    .then((data) => {
                        this.$loader.fadeOut(150);
                        this.renderSuccessResult(data);
                        this.sendToAnalytics(e, data);

                        // update valtagmanager
                        if (window.digitalData) {
                            window.digitalData.update();
                        }
                    })
                    .catch((data) => {
                        this.$loader.fadeOut(150);
                        this.renderErrorResult(data);
                    })
                    .finally(() => {
                        this.showCollapse();
                    });
            }
        });
    }

    getData() {
        return new Promise((resolve, reject) => {
            this.ajax
                .request({
                    url: '/' + this.config.lang + '/' + this.config.apiSearch,
                    data: JSON.stringify({ chassisnumber: this.searchedChassisNumber, itemId: this.$itemId.val() }),
                })
                .then((response) => {
                    if (response.ResultsFound) {
                        resolve(response);
                    } else {
                        reject(response);
                    }
                })
                .catch(() => {
                    reject();
                });
        });
    }

    renderSuccessResult({ ResultTitle, ResultBody, HasUpgrades, VehicleUpgrades, FollowUp }) {
        this.$error.hide();
        this.$results.show();

        this.renderResultsTitle(ResultTitle);
        this.renderVehicleInformation(ResultBody);
        this.renderVehicleUpgrades(HasUpgrades, VehicleUpgrades);
        this.renderFollowUp(FollowUp.Text, FollowUp.Actions);
    }

    renderErrorResult({ NoResults }) {
        this.$error.text(NoResults);
        this.$error.show();
        this.$results.hide();
        this.$followUp.hide();
    }

    renderResultsTitle(title) {
        this.$resultTitle.html(title);
    }

    renderVehicleInformation(body) {
        this.$resultBody.html(body);
    }

    renderVehicleUpgrades(hasUpgrades, upgrades) {
        this.$vehicleUpgrades.html(upgrades).attr('data-has-upgrades', hasUpgrades);
    }

    renderFollowUp(text, actions) {
        this.$followUp.show();
        this.$followUpText.text(text);
        this.$followUpActions.html(
            actions.reduce((html, action) => {
                return html + `<a class="btn btn-primary" href="${action.Link}">${action.Text}</a>`;
            }, '')
        );
    }

    showCollapse() {
        if (!this.$element.hasClass('is-open')) {
            this.$element.addClass('is-open');
            this.collapsible.collapse('show');
        }
    }

    sendToAnalytics(e, data) {
        const { currentTarget } = e;
        currentTarget.dataset.dmEventAction = this.searchedChassisNumber;
        currentTarget.dataset.dmElementValue = data.VehicleUpgrades;

        window.digitalData.handleEvent({ type: 'submit', currentTarget });
    }
}

// register to Component Handler
window.ComponentHandler.register({
    constructor: PaccarPartsApiSearch,
    classAsString: 'PaccarPartsApiSearch',
    cssClass: 'js-parts-search',
});

export default PaccarPartsApiSearch;
